import React, { useEffect, useState } from 'react';
import AnnAvatar from '../../../images/icon_dark_bg.png';
import UserAvatar from '../../../images/UserAvatar.svg';
import ReactMarkdown from 'react-markdown';
import {
  MessageContainer, 
  MessageWrapper, 
  ChatWindowAvatar,
  MessageButtonsWrapper,
  EditMessageWrapper,
  EditMessageText,
  EditMessageInput,
  EditMessageInputWrapper,
  MessageInfo,
} from '../styles/ChatWindowComponent.styled';
import { Icon } from "../../../elements/icon";
import { RoundedButton } from '../../../elements';
import { observer } from "mobx-react";
import { useStore } from '../../../hooks';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';
import DOMPurify from 'dompurify';
import styled from 'styled-components';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

// New component to render HTML content within a Shadow DOM
const ShadowContent = ({ htmlContent }) => {
  const hostRef = React.useRef(null);

  React.useEffect(() => {
    if (hostRef.current) {
      // Check if shadowRoot already exists
      if (!hostRef.current.shadowRoot) {
        const shadowRoot = hostRef.current.attachShadow({ mode: 'open' });
        shadowRoot.innerHTML = htmlContent;
      } else {
        // If shadowRoot exists, just update its content
        // This handles potential re-renders if htmlContent changes, though not strictly necessary
        // if htmlContent is guaranteed to be stable for the component's lifecycle.
        hostRef.current.shadowRoot.innerHTML = htmlContent;
      }
    }
  }, [htmlContent]); // Re-run if htmlContent changes

  return <div ref={hostRef} className="html-content-host"></div>;
};

const CopyIconWrapper = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  background-color: ${props => props.theme.colors.greySix};
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${props => props.theme.colors.greySix};
  }

  svg {
    font-size: 16px;
    color: ${props => props.theme.colors.darkCharcoal};
  }
`;

const MessageWrapperWithCopy = styled(MessageWrapper)`
  position: relative;

  &:hover ${CopyIconWrapper} {
    opacity: 1;
  }
`;

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (!bytes || isNaN(bytes)) return 'Unknown size';
  if (bytes < 1024) return bytes + ' bytes';
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  else return (bytes / 1048576).toFixed(1) + ' MB';
};

const Message = observer(({ messageItem, key, displayButtons, width, selectedAgent }) => {
  const [displayMessageButtons, setDisplayMessageButtons] = useState(displayButtons)
  const [displayMessageView, setDisplayMessageView] = useState(null)
  const [copySuccess, setCopySuccess] = useState(false);
  const { profileStore } = useStore();

  const createMarkup = (content) => {
    return { __html: DOMPurify.sanitize(content, {
      ALLOWED_ATTR: ['class', 'id', 'style'],
      ALLOWED_CSS_PROPERTIES: [
        'color',
        'background-color',
        'font-weight',
        'font-style',
        'text-decoration',
        'text-align',
        'font-size'
      ]
    }) };
  };

  const isHTML = (str) => {
    const doc = new DOMParser().parseFromString(str, "text/html");
    return Array.from(doc.body.childNodes).some(node => node.nodeType === 1);
  };

  // Render file attachment in the message
  const renderFileAttachment = (files) => {
    if (!files || !Array.isArray(files) || files.length === 0) return null;
    
    return (
      <div className="file-attachments">
        {files.map((file, index) => {
          // Determine file type icon
          let iconName = "file";
          if (file.type && file.type.includes('pdf')) {
            iconName = "fileV2";
          } else if (file.type && (file.type.includes('word') || file.type.includes('document'))) {
            iconName = "wordLogo";
          } else if (file.type && (file.type.includes('spreadsheet') || file.type.includes('excel'))) {
            iconName = "excelLogo";
          }
          
          return (
            <div key={index} className="file-attachment">
              <Icon name={iconName} height="22px" width="22px" />
              <div className="file-info">
                <div className="file-name">{file.name}</div>
                <div className="file-meta">
                  {file.type ? file.type.split('/').pop() : 'File'} • {file.size || formatFileSize(file.size)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderContent = (message) => {
    // Add type validation to prevent rendering objects
    if (typeof message !== 'string') {
      console.error('Message is not a string:', message);
      // Try to extract string from object
      if (typeof message === 'object' && message !== null) {
        if (message.message) return renderContent(message.message);
        if (message.content) return renderContent(message.content);
        if (message.text) return renderContent(message.text);
        // Fallback: stringify the object for debugging
        return (
          <div style={{ color: 'red', padding: '10px', border: '1px solid red' }}>
            Error rendering message: Invalid message format
            <details style={{ marginTop: '10px', fontSize: '12px' }}>
              <summary>Debug info</summary>
              <pre>{JSON.stringify(message, null, 2)}</pre>
            </details>
          </div>
        );
      }
      return <div style={{ color: 'red' }}>Unable to display message</div>;
    }

    if (isHTML(message)) {
      // Use the ShadowContent component for HTML messages
      return <ShadowContent htmlContent={DOMPurify.sanitize(message, {
        ALLOWED_ATTR: ['class', 'id', 'style'],
        ALLOWED_CSS_PROPERTIES: [
          'color',
          'background-color',
          'font-weight',
          'font-style',
          'text-decoration',
          'text-align',
          'font-size'
          // Consider removing 'background-color' from here if it's a persistent issue
          // or ensure the app's global styles for body/html are more specific.
        ]
      })} />;
    }

    return (
      <ReactMarkdown
        remarkPlugins={[
          remarkGfm,
          remarkParse,
          [remarkRehype, {
            singleTilde: true,
            stringLength: true,
            tablePipeAlign: true,
            tableCellPadding: true
          }]
        ]}
        rehypePlugins={[
          rehypeRaw,
          rehypeStringify,
          [rehypeSanitize, {
            allowElements: [
              'p', 'br', 'b', 'i', 'em', 'strong', 'a', 'img',
              'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
              'ul', 'ol', 'li', 'blockquote', 'code', 'pre',
              'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
              'div', 'span', 'del'
            ],
            allowAttributes: {
              'a': ['href', 'target', 'rel'],
              'img': ['src', 'alt', 'title'],
              'td': ['align'],
              'th': ['align'],
              '*': ['class']
            }
          }]
        ]}
      >
        {message}
      </ReactMarkdown>
    );
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(messageItem.message);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return(
      <MessageContainer key={key} itemType={messageItem.type} width={width}>
        { messageItem.type === "sent" ?
            <ChatWindowAvatar src={profileStore?.displayPhotoURL || UserAvatar} alt="Agent Avatar" /> :
            <ChatWindowAvatar src={selectedAgent?.photo_url || AnnAvatar} alt="Agent Avatar" />
        }

        {!displayMessageView &&
            <MessageWrapperWithCopy itemType={messageItem.type}>
              {messageItem.type === "file" ? (
                <>
                  <div className="file-message">{messageItem.content || "Attached file(s):"}</div>
                  {renderFileAttachment(messageItem.files)}
                </>
              ) : (
                <>
                  {renderContent(messageItem.message)}
                  {messageItem.hasAttachment && (
                    <div className="attachment-reference">
                      <Icon name="attachmentIcon" height="14px" width="14px" />
                      <span>
                        {messageItem.fileReference && messageItem.fileReference.length > 1 
                          ? `${messageItem.fileReference.length} files attached` 
                          : `File attached: ${messageItem.fileReference ? messageItem.fileReference[0] : 'file'}`}
                      </span>
                    </div>
                  )}
                </>
              )}
              {messageItem.type !== "sent" && messageItem.type !== "file" && (
                <CopyIconWrapper onClick={handleCopy} title={copySuccess ? "Copied!" : "Copy message"}>
                  {copySuccess ? <CheckIcon /> : <ContentCopyIcon />}
                </CopyIconWrapper>
              )}
            </MessageWrapperWithCopy>
        }

        {messageItem.type !== 'sent' && displayMessageView === 'initial' && (
            <EditMessageWrapper>
              <Icon name={"head1"} height={'120px'} width={'120px'} />
              <EditMessageText>Help me to improve!</EditMessageText>
              <RoundedButton width='20%' onClick={() => setDisplayMessageView('edit')}>Train me</RoundedButton>
            </EditMessageWrapper>
        )}

        {messageItem.type !== 'sent' && displayMessageView === 'edit' && (

            <EditMessageInputWrapper>
              <EditMessageInput placeholder='Feel free to enter the correct answer right here.' rows="10" cols="50"></EditMessageInput>

              <MessageButtonsWrapper mt='5px'>
                <Icon name={"thumbsDown"} height={'15px'} width={'15px'} />
                <RoundedButton width='150px' height='40px' fontSize='15px' onClick={() => {setDisplayMessageView(null);setDisplayMessageButtons(false)}}>Train</RoundedButton>
              </MessageButtonsWrapper>
            </EditMessageInputWrapper>
        )}
      </MessageContainer>
  );
});

export default Message;
